import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Button, alert } from 'equasion-lib';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import type { UpcLinkOffer } from '@/lib/scanning/productUpcLink';

export const PRODUCT_UPC_LINK = gql`
  mutation PickProductUpcLink($companyId: ID!, $productId: ID!, $upc: String!) {
    productUpcLink(companyId: $companyId, productId: $productId, upc: $upc) {
      id
      upcs
    }
  }
`;

type PickUpcLinkPanelProps = {
  offer: UpcLinkOffer;
  companyId: string;
  /** The UPC is now linked: the sheet re-sends the unit, which now matches the line. */
  onLinked: () => void;
  onCancel: () => void;
};

const click = (onClick: () => void) => ({ onClick });

/**
 * "UPC not recognized" on an OTC line, for a user who may update products: link the scanned UPC to
 * the line's product on the spot (`productUpcLink`, which keeps the product's other UPCs and refuses
 * a UPC already on another product). Every later scan of it then finds this product.
 */
function PickUpcLinkPanel({ offer, companyId, onLinked, onCancel }: PickUpcLinkPanelProps) {
  const [linkUpc] = useMutation(PRODUCT_UPC_LINK);
  const [busy, setBusy] = useState(false);

  const link = async () => {
    setBusy(true);
    try {
      await linkUpc({ variables: { companyId, productId: offer.productId, upc: offer.upc } });
      alert('UPC linked', `UPC ${offer.upc} now scans as ${offer.productName}.`, 4, 'success');
      onLinked();
    } catch (e) {
      alert('Could not link UPC', operatorFacingMessage(e, 'Could not link the UPC. Please try again.'), 6, 'error');
      setBusy(false);
    }
  };

  return (
    <div className="pick-upc-link" role="group" aria-label="Link UPC">
      <h2>UPC not recognized</h2>
      <p>
        UPC <strong>{offer.upc}</strong> is not linked to any product. Link it to{' '}
        <strong>{offer.productName}</strong>
        {offer.ndc ? ` (NDC ${offer.ndc})` : ''}? Only do this if the unit in your hand is this
        product.
      </p>
      <div className="pick-upc-link__actions">
        <Button type="primary" fullWidth disabled={busy} {...click(() => void link())}>
          {busy ? 'Linking…' : 'Link UPC to this product'}
        </Button>
        <Button type="secondary" fullWidth disabled={busy} {...click(onCancel)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default PickUpcLinkPanel;
